import { create } from 'zustand';
import { isAxiosError } from 'axios';
import { useCases } from '../../infrastructure/di/container';
import { getErrorMessage } from '../../infrastructure/http/common';
import {
  DeliveryAlerts,
  DeliveryEvent,
  DeliveryEventDetail,
  DeliveryEventFilters,
  DeliveryMatrixGroupBy,
  DeliveryMatrixRow,
  DeliverySummary,
  DeliveryWindow,
  deliveryWindowAround,
} from '../../domain/entities/DeliveryDashboard';

const DEFAULT_RANGE_DAYS = 7;
const EVENTS_PER_PAGE = 50;

const toErrorSlug = (e: unknown): string => {
  if (isAxiosError(e)) return getErrorMessage(e);
  if (e instanceof Error && e.message) return e.message;
  return 'unknown-error';
};

const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

interface DeliveryDashboardState {
  rangeDays: number;
  window: DeliveryWindow;
  groupBy: DeliveryMatrixGroupBy;
  summary?: DeliverySummary;
  alerts?: DeliveryAlerts;
  matrix: DeliveryMatrixRow[];
  filters: DeliveryEventFilters;
  events: DeliveryEvent[];
  eventsTotal: number;
  eventsPage: number;
  detail?: DeliveryEventDetail;
  isLoading: boolean;
  isEventsLoading: boolean;
  updatedAt?: Date;
  error?: string;
  loadOverview: () => Promise<void>;
  loadEvents: () => Promise<void>;
  refresh: () => Promise<void>;
  setRangeDays: (days: number) => Promise<void>;
  setGroupBy: (groupBy: DeliveryMatrixGroupBy) => Promise<void>;
  setFilters: (filters: Partial<DeliveryEventFilters>) => Promise<void>;
  clearFilters: () => Promise<void>;
  setEventsPage: (page: number) => Promise<void>;
  openEvent: (uuid: string) => Promise<void>;
  closeEvent: () => void;
}

const EMPTY_FILTERS: DeliveryEventFilters = { outcomes: [] };

export const useDeliveryDashboardStore = create<DeliveryDashboardState>((set, get) => ({
  rangeDays: DEFAULT_RANGE_DAYS,
  window: deliveryWindowAround(new Date(), DEFAULT_RANGE_DAYS),
  groupBy: 'account',
  matrix: [],
  filters: EMPTY_FILTERS,
  events: [],
  eventsTotal: 0,
  eventsPage: 1,
  isLoading: false,
  isEventsLoading: false,
  async loadOverview() {
    const { window, groupBy } = get();
    set({ isLoading: true, error: undefined });
    try {
      const overview = await useCases.loadDeliveryOverview().execute(window, groupBy, browserTimezone());
      set({ ...overview, isLoading: false, updatedAt: new Date() });
    } catch (e) {
      set({ isLoading: false, error: toErrorSlug(e) });
    }
  },
  async loadEvents() {
    const { window, filters, eventsPage } = get();
    set({ isEventsLoading: true });
    try {
      const page = await useCases.listDeliveryEvents().execute({ window, filters, page: eventsPage, perPage: EVENTS_PER_PAGE });
      set({ events: page.events, eventsTotal: page.total, isEventsLoading: false });
    } catch (e) {
      set({ isEventsLoading: false, error: toErrorSlug(e) });
    }
  },
  async refresh() {
    set({ window: deliveryWindowAround(new Date(), get().rangeDays) });
    await Promise.all([get().loadOverview(), get().loadEvents()]);
  },
  async setRangeDays(rangeDays) {
    set({ rangeDays, eventsPage: 1, filters: { ...get().filters, day: undefined } });
    await get().refresh();
  },
  async setGroupBy(groupBy) {
    set({ groupBy });
    await get().loadOverview();
  },
  async setFilters(filters) {
    set({ filters: { ...get().filters, ...filters }, eventsPage: 1 });
    await get().loadEvents();
  },
  async clearFilters() {
    set({ filters: EMPTY_FILTERS, eventsPage: 1 });
    await get().loadEvents();
  },
  async setEventsPage(eventsPage) {
    set({ eventsPage });
    await get().loadEvents();
  },
  async openEvent(uuid) {
    try {
      set({ detail: await useCases.getDeliveryEvent().execute(uuid) });
    } catch (e) {
      set({ error: toErrorSlug(e) });
    }
  },
  closeEvent() {
    set({ detail: undefined });
  },
}));

export const DELIVERY_EVENTS_PER_PAGE = EVENTS_PER_PAGE;
